import type { DriverAccountResponse } from "@turo/types-dunlop";
import type { CountryCode } from "libphonenumber-js/mobile";
import { useEffect, useState } from "react";
import type { ValueOf } from "type-fest";

import { initializeRecaptcha } from "~/src/helpers/recaptcha";

import PhoneConfirmOTPForm from "./PhoneConfirmOTPForm";
import PhoneLoginOTPForm from "./PhoneLoginOTPForm";

const PHONE_LOGIN_STEPS = {
  GENERATE_OTP: "GENERATE_OTP",
  CONFIRM_OTP: "CONFIRM_OTP",
} as const;

export interface OnLoginFormSubmitSuccessOptions {
  countryCode: CountryCode;
  otpToken: string;
  phoneNumber: string;
}

interface PhoneLoginViewProps {
  loginStart: () => void;
  loginSuccess: (data: DriverAccountResponse) => void;
  otpTokenLoginStart: () => void;
}

function PhoneLoginView({
  loginStart,
  loginSuccess,
  otpTokenLoginStart,
}: PhoneLoginViewProps) {
  const [phoneLoginStep, setPhoneLoginStep] = useState<
    ValueOf<typeof PHONE_LOGIN_STEPS>
  >(PHONE_LOGIN_STEPS.GENERATE_OTP);
  const [countryCode, setCountryCode] = useState<string | undefined>(undefined);
  const [otpToken, setOtpToken] = useState<string | undefined>(undefined);
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);

  const areAllConfirmFormPropsPresent =
    !!otpToken && !!phoneNumber && !!countryCode;

  /**
   * We use this effect to ensure that the props we're threading to
   * `PhoneConfirmOTPForm` are non-nullish. This is effectively an
   * app-wide type guard because we set all of these properties
   * at the same time.
   */
  useEffect(() => {
    if (
      phoneLoginStep === PHONE_LOGIN_STEPS.CONFIRM_OTP &&
      !areAllConfirmFormPropsPresent
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Error: Calling setState synchronously within an effect can trigger cascading renders. See more: https://team-turo.atlassian.net/wiki/spaces/EN/pages/3656941682/How-to+React+Guide#react-hooks%2Fset-state-in-effect
      setPhoneLoginStep(PHONE_LOGIN_STEPS.GENERATE_OTP);
    }
  }, [phoneLoginStep, areAllConfirmFormPropsPresent]);

  useEffect(() => {
    initializeRecaptcha();
  }, []);

  const handleClickConfirmOTPFormBack = () => {
    setPhoneLoginStep(PHONE_LOGIN_STEPS.GENERATE_OTP);
  };

  const onLoginFormSubmitSuccess = (
    options: OnLoginFormSubmitSuccessOptions,
  ) => {
    setPhoneLoginStep(PHONE_LOGIN_STEPS.CONFIRM_OTP);
    setCountryCode(options.countryCode);
    setOtpToken(options.otpToken);
    setPhoneNumber(options.phoneNumber);
  };

  if (
    phoneLoginStep === PHONE_LOGIN_STEPS.CONFIRM_OTP &&
    areAllConfirmFormPropsPresent
  ) {
    return (
      <PhoneConfirmOTPForm
        countryCode={countryCode}
        loginStart={loginStart}
        loginSuccess={loginSuccess}
        onClickBack={handleClickConfirmOTPFormBack}
        onResendOtpTokenSuccess={setOtpToken}
        otpToken={otpToken}
        phoneNumber={phoneNumber}
      />
    );
  }

  return (
    <PhoneLoginOTPForm
      onLoginFormSubmitSuccess={onLoginFormSubmitSuccess}
      otpTokenLoginStart={otpTokenLoginStart}
    />
  );
}

export default PhoneLoginView;
